import type { PointerEvent } from "react";
import {
  POPOVER_LIST_ROOT_ATTR,
  POPOVER_LIST_SEARCH_ATTR,
} from "./popover-list-search-attrs";

/**
 * Radix Menu focuses the row under the pointer and, on leave, the menu root.
 * That yanks the caret out of the list search. `preventDefault` skips those
 * handlers when this overlay has a search field (Radix checks it).
 */
export function stopSearchableMenuPointerFocus(
  event: PointerEvent<Element>,
): void {
  const node = event.currentTarget;
  if (!(node instanceof Element)) return;
  const root = node.closest(`[${POPOVER_LIST_ROOT_ATTR}]`);
  if (root == null) return;
  if (root.querySelector(`[${POPOVER_LIST_SEARCH_ATTR}]`) == null) return;
  event.preventDefault();
}

export interface SearchableMenuItemPointerHandlers<T extends Element> {
  readonly onPointerMove: (event: PointerEvent<T>) => void;
  readonly onPointerLeave: (event: PointerEvent<T>) => void;
}

/**
 * Spread after item `{...props}` so Radix sees these as `props.onPointerMove`
 * / `onPointerLeave` and skips item/root focus when default is prevented.
 */
export function searchableMenuItemPointerProps<T extends Element>(props: {
  readonly onPointerMove?: (event: PointerEvent<T>) => void;
  readonly onPointerLeave?: (event: PointerEvent<T>) => void;
}): SearchableMenuItemPointerHandlers<T> {
  return {
    onPointerMove: (event: PointerEvent<T>): void => {
      props.onPointerMove?.(event);
      stopSearchableMenuPointerFocus(event);
    },
    onPointerLeave: (event: PointerEvent<T>): void => {
      props.onPointerLeave?.(event);
      stopSearchableMenuPointerFocus(event);
    },
  };
}
